// src/App.js - Synchro ventes -> stock + Clients + Analytics + Legal + Charges + BackToTop + ThemeProvider (complet)

import React, { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';

// Critiques pour le rendu initial
import Homepage from './components/Homepage';
import Protected from './components/Protected';
import BackToTop from './components/common/BackToTop';
import OfflineIndicator from './components/OfflineIndicator';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import useKeyboardWedge from './components/hooks/useKeyboardWedge';

// Contexte & styles
import { UserRoleProvider, useUserRole } from './contexts/UserRoleContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { ToastProvider } from './components/Toast';
import './styles/main.css';

// Navbar (lazy — MUI n'est pas nécessaire sur les pages publiques)
const Navbar = lazy(() => import('./components/Navbar'));

// Auth pages (lazy — seulement quand l'utilisateur navigue vers ces routes)
const Login            = lazy(() => import('./components/auth/Login'));
const Register         = lazy(() => import('./components/auth/Register'));
const AcceptInvitation = lazy(() => import('./components/auth/AcceptInvitation'));

// Composants post-auth : chargés seulement quand l'utilisateur est connecté
const FloatingNav      = lazy(() => import('./components/FloatingNav'));
const ScreenSaver      = lazy(() => import('./components/ScreenSaver'));

// Pages lourdes en lazy loading (chargées uniquement quand l'utilisateur y accède)
const Dashboard          = lazy(() => import('./components/dashboard/Dashboard'));
const Achats             = lazy(() => import('./components/achats/Achats'));
const StockManagement    = lazy(() => import('./components/stock/StockManagement'));
const OrderManagement    = lazy(() => import('./components/stock/OrderManagement'));
const Ventes             = lazy(() => import('./components/ventes/Ventes'));
const Parametres         = lazy(() => import('./components/parametres/Parametres'));
const DevisFactures      = lazy(() => import('./components/devisFactures/DevisFactures'));
const Paiements          = lazy(() => import('./components/paiements/Paiements'));
const BackupExport       = lazy(() => import('./components/BackupExport'));
const ImportBackup       = lazy(() => import('./components/ImportBackup'));
const UsersManagement    = lazy(() => import('./components/users/UsersManagement'));
const GestionUtilisateurs    = lazy(() => import('./components/admin/GestionUtilisateurs'));
const AddSocieteIdToAllUsers = lazy(() => import('./components/admin/AddSocieteIdToAllUsers'));
const InitOwner          = lazy(() => import('./components/admin/InitOwner'));
const AgentMonitor       = lazy(() => import('./components/admin/AgentMonitor'));
const Clients            = lazy(() => import('./components/clients/Clients'));
const ClotureCaisse      = lazy(() => import('./components/caisse/ClotureCaisse'));
const Analytics          = lazy(() => import('./components/analytics/Analytics'));
const LegalDocuments     = lazy(() => import('./components/legal/LegalDocuments'));
const ChargesPersonnels  = lazy(() => import('./components/charges/ChargesPersonnels'));
const ChargesDivers      = lazy(() => import('./components/charges/ChargesDivers'));
const RapportJournalier   = lazy(() => import('./components/rapportJournalier/RapportJournalier'));
const LandingPage         = lazy(() => import('./pages/LandingPage'));

// Pré-charge les chunks critiques — seulement quand l'utilisateur est authentifié
function usePrefetchChunks(isAuthenticated) {
  useEffect(() => {
    if (!isAuthenticated || !navigator.onLine) return;
    const imports = [
      () => import('./components/ventes/Ventes'),
      () => import('./components/achats/Achats'),
      () => import('./components/stock/StockManagement'),
      () => import('./components/charges/ChargesDivers'),
      () => import('./components/charges/ChargesPersonnels'),
      () => import('./components/paiements/Paiements'),
      () => import('./components/analytics/Analytics'),
    ];
    const prefetch = () => imports.forEach(fn => fn().catch(() => {}));
    if ('requestIdleCallback' in window) {
      const id = requestIdleCallback(prefetch, { timeout: 15000 });
      return () => cancelIdleCallback(id);
    }
    const t = setTimeout(prefetch, 12000);
    return () => clearTimeout(t);
  }, [isAuthenticated]);
}

// Fallback léger affiché pendant le chargement d'une page lazy
const PageLoader = () => (
  <div style={{
    minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'linear-gradient(135deg,#0b1220,#1a2540)', color: '#7ee4e6'
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{
        width: 20, height: 20, border: '3px solid rgba(126,228,230,.3)',
        borderTop: '3px solid #7ee4e6', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite'
      }} />
      <span style={{ fontWeight: 600 }}>Chargement...</span>
    </div>
    <style>{`@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
  </div>
);

/* -------------------------------------------
 * Placeholders Commandes
 * -----------------------------------------*/
// eslint-disable-next-line react/prop-types
const PlaceholderCard = ({ title, text, hint }) => (
  <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', background: '#f6f8fa', padding: 24 }}>
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: 24, maxWidth: 720 }}>
      <h2 style={{ margin: '0 0 8px' }}>{title}</h2>
      <p style={{ margin: 0, color: '#6b7280' }}>{text}</p>
      {hint && (
        <div style={{ marginTop: 12 }}>
          <code style={{ background: '#f3f4f6', padding: '6px 8px', borderRadius: 8, display: 'inline-block' }}>{hint}</code>
        </div>
      )}
    </div>
  </div>
);

const Commandes = () => (
  <PlaceholderCard
    title="Commandes (liste/gestion)"
    text="Crée src/components/commandes/Commandes.js puis remets l'import dans App.js."
    hint="src/components/commandes/Commandes.js"
  />
);

const NouvelleCommande = () => (
  <PlaceholderCard
    title="Nouvelle commande"
    text="Choisir un produit existant du stock ou créer un nouveau ; si le produit existe (même avec quantité 0), afficher la quantité en 'état stock'."
    hint="src/components/commandes/NouvelleCommande.js"
  />
);

/* -------------------------------------------
 * Navigation Stock (onglets)
 * -----------------------------------------*/
function StockPage() {
  const [activeTab, setActiveTab] = useState('stock');

  return (
    <div style={{ minHeight: '100vh' }}>
      <div style={{
        background: 'linear-gradient(135deg,#1f2937,#111827)', padding: '16px 20px',
        display: 'flex', gap: 12, position: 'sticky', top: 0, zIndex: 100
      }}>
        <button
          onClick={() => setActiveTab('stock')}
          style={{
            padding: '12px 24px', borderRadius: 12,
            background: activeTab === 'stock' ? 'linear-gradient(135deg,#6366f1,#a855f7)' : 'rgba(255,255,255,.1)',
            color: '#fff', fontWeight: 700, cursor: 'pointer', border: 'none'
          }}
        >
          Gestion du Stock
        </button>

        <button
          onClick={() => setActiveTab('orders')}
          style={{
            padding: '12px 24px', borderRadius: 12,
            background: activeTab === 'orders' ? 'linear-gradient(135deg,#6366f1,#a855f7)' : 'rgba(255,255,255,.1)',
            color: '#fff', fontWeight: 700, cursor: 'pointer', border: 'none'
          }}
        >
          Commandes à Passer
        </button>
      </div>

      {activeTab === 'stock' && <StockManagement />}
      {activeTab === 'orders' && <OrderManagement />}
    </div>
  );
}

/* -------------------------------------------
 * Pages annexes
 * -----------------------------------------*/
function BackupPage() {
  const [currentTab, setCurrentTab] = useState('export');

  return (
    <div className="fullscreen-table-wrap">
      <div className="fullscreen-table-title">Gestion des Sauvegardes</div>
      <div style={{
        display: 'flex', justifyContent: 'center', marginBottom: 20,
        background: '#2d3748', borderRadius: 10, padding: 5, maxWidth: 400, margin: '0 auto 20px'
      }}>
        <button onClick={() => setCurrentTab('export')} style={{
          flex: 1, padding: '12px 20px',
          background: currentTab === 'export' ? '#4CAF50' : 'transparent',
          color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
          fontWeight: currentTab === 'export' ? 'bold' : 'normal'
        }}>Export</button>
        <button onClick={() => setCurrentTab('import')} style={{
          flex: 1, padding: '12px 20px',
          background: currentTab === 'import' ? '#2196F3' : 'transparent',
          color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer',
          fontWeight: currentTab === 'import' ? 'bold' : 'normal'
        }}>Import</button>
      </div>

      {currentTab === 'export' ? <BackupExport /> : <ImportBackup />}
    </div>
  );
}

function UsersPage() {
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#667eea,#764ba2)' }}>
      <UsersManagement />
    </div>
  );
}

function GestionRolesPage() {
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#223049,#344060)' }}>
      <GestionUtilisateurs />
    </div>
  );
}

/* -------------------------------------------
 * Wrapper Routes (Navbar)
 * -----------------------------------------*/
function AppWrapper() {
  const navigate = useNavigate();
  const location = useLocation();
  const { societeId } = useUserRole();
  usePrefetchChunks(!!societeId);
  const { darkMode } = useTheme();

  // Scanner global : si scan depuis n'importe quelle page sauf /ventes et /achats
  // → stocker le code et naviguer vers /ventes
  useKeyboardWedge((code) => {
    if (location.pathname === '/ventes') return; // Ventes gère lui-même
    if (location.pathname === '/achats') return;  // Achats gère lui-même
    if (location.pathname === '/stock') return;   // Stock gère lui-même
    window.__pendingGlobalScan = code;
    navigate('/ventes');
  }, { minChars: 6, timeoutMs: 250, scannerSpeedMs: 60 });



  useEffect(() => {
    const SHORTCUTS = { a: '/achats', v: '/ventes', s: '/stock', d: '/dashboard', p: '/paiements' };
    const handler = (e) => {
      const el = document.activeElement;
      const tag = el?.tagName?.toLowerCase();
      // Bloquer les raccourcis si focus dans un champ de saisie (input, textarea, select, MUI, contenteditable)
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (el?.isContentEditable) return;
      if (el?.closest('[role="textbox"],[role="combobox"],[role="listbox"],[role="spinbutton"]')) return;
      if (el?.closest('.MuiInputBase-root, .MuiAutocomplete-root, .MuiSelect-root')) return;
      if (e.ctrlKey || e.altKey || e.metaKey) return;
      const key = e.key?.toLowerCase();
const target = SHORTCUTS[key];
      if (target && location.pathname !== target) navigate(target);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [navigate, location.pathname]);
  const hideNavbar = [
    '/',
    '/login',
    '/register',
    '/accept-invitation',
    '/legal'
  ].includes(location.pathname) || (location.pathname.startsWith('/admin-') && location.pathname !== '/admin-agent');

  return (
    <>
      {societeId && <Suspense fallback={null}><ScreenSaver /></Suspense>}
      {societeId && <Suspense fallback={null}><FloatingNav /></Suspense>}
      {!hideNavbar && <Suspense fallback={<div style={{height:64,background:'#1a2540'}} />}><Navbar /></Suspense>}
      <div style={{
        minHeight: '100vh',
        background: hideNavbar && (location.pathname === '/' || location.pathname === '/legal')
          ? 'transparent'
          : darkMode ? '#0f172a' : '#f6f8fa',
      }}>
        <ChunkErrorBoundary>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Homepage onLogin={() => (window.location.href = '/login')} onRegister={() => (window.location.href = '/register')} />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />

          {/* Documents Légaux - accessible SANS connexion */}
          <Route path="/legal" element={<LegalDocuments />} />

          <Route path="/dashboard" element={<Protected permission="voir_dashboard"><Dashboard /></Protected>} />
          <Route path="/achats" element={<Protected permission="voir_achats"><Achats /></Protected>} />
          <Route path="/ventes" element={<Protected permission="voir_ventes"><Ventes /></Protected>} />
          <Route path="/stock" element={<Protected permission="ajouter_stock"><StockPage /></Protected>} />
          <Route path="/clients" element={<Protected permission="voir_ventes"><Clients /></Protected>} />
          <Route path="/commandes" element={<Protected permission="voir_ventes"><Commandes /></Protected>} />
          <Route path="/commandes/nouveau" element={<Protected permission="voir_ventes"><NouvelleCommande /></Protected>} />
          <Route path="/devis-factures" element={<Protected permission="voir_ventes"><DevisFactures /></Protected>} />
          <Route path="/paiements" element={<Protected permission="voir_ventes"><Paiements /></Protected>} />

          {/* Modules Charges */}
          <Route path="/charges-personnels" element={<Protected permission="voir_dashboard"><ChargesPersonnels /></Protected>} />
          <Route path="/charges-divers" element={<Protected permission="voir_dashboard"><ChargesDivers /></Protected>} />

          {/* Analytics - Statistiques */}
          <Route path="/analytics" element={<Protected permission="voir_dashboard"><Analytics /></Protected>} />


          {/* Clôture caisse */}
          <Route path="/cloture" element={<Protected permission="voir_paiements"><ClotureCaisse /></Protected>} />

          <Route path="/parametres" element={<Protected permission="parametres"><Parametres /></Protected>} />
          <Route path="/backup" element={<Protected permission="voir_dashboard"><BackupPage /></Protected>} />
          <Route path="/import" element={<Protected permission="voir_dashboard"><div className="fullscreen-table-wrap"><div className="fullscreen-table-title">Import de Sauvegarde</div><ImportBackup /></div></Protected>} />
          <Route path="/gestion-utilisateurs" element={<Protected permission="gerer_utilisateurs"><GestionRolesPage /></Protected>} />
          <Route path="/users" element={<Protected permission="parametres"><UsersPage /></Protected>} />

          <Route path="/rapport-journalier" element={<Protected permission="voir_dashboard"><RapportJournalier /></Protected>} />
          <Route path="/abonnement" element={<Navigate to="/dashboard" replace />} />
          <Route path="/landing" element={<LandingPage />} />

          <Route path="/admin-agent" element={<AgentMonitor />} />

          <Route path="/admin-init-owner" element={<InitOwner />} />
          <Route path="/admin-update-societe" element={<AddSocieteIdToAllUsers />} />

          <Route path="/societe" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        </ChunkErrorBoundary>

        <BackToTop threshold={400} />
        <OfflineIndicator />
      </div>
    </>
  );
}

/* -------------------------------------------
 * App racine avec ThemeProvider
 * -----------------------------------------*/
function App() {
  return (
    <UserRoleProvider>
      <ThemeProvider>
        <ToastProvider>
          <Router>
            <AppWrapper />
          </Router>
        </ToastProvider>
      </ThemeProvider>
    </UserRoleProvider>
  );
}

export default App;
